import type { ProbeLevel, TargetProbeLegRecord } from '@nadoutong/contracts';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import type { I18nKey, TParams } from '../../i18n/index.ts';

type Translate = (key: I18nKey, params?: TParams) => string;

interface RegionSlice {
  readonly region: string;
  readonly slice: TargetProbeLegRecord;
}

export type LevelPillKind = 'alert' | 'war' | 'cooldown';

export interface LevelPill {
  readonly region: string;
  readonly kind: LevelPillKind;
  readonly text: string;
  readonly tip: string;
}

function futureMs(iso: string | null | undefined, nowMs: number): number | null {
  if (typeof iso !== 'string') return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) && ms > nowMs ? ms : null;
}

function clock(iso: string): string {
  return formatLocalTimestamp(iso).slice(11, 16);
}

/**
 * The region's level: a war with `war_until` still ahead wins over whatever
 * the last level event said (the event lags the burst runner by a write);
 * otherwise the level of the latest `probe_level` event. Null = 常规.
 */
export function sliceLevel(slice: TargetProbeLegRecord, nowMs: number): ProbeLevel | null {
  if (futureMs(slice.war_until, nowMs) !== null) return 'war';
  const level = slice.level ?? null;
  if (level === 'war') return 'alert';
  return level === 'normal' ? null : level;
}

/**
 * Level pills for one physical leg, per region (#2658 follow-up): 战争 with
 * the wave progress, 警戒 since when, and the war cooldown with its count.
 * 常规 shows nothing. The region tag is prefixed only when the leg has more
 * than one region slice.
 */
export function legLevelPills(
  slices: readonly RegionSlice[],
  nowMs: number,
  t: Translate,
  /** The owning SKU's probe mode is not off. Off: no pill at all — any level left in Redis or the events is residue. */
  probesOn: boolean,
): readonly LevelPill[] {
  if (!probesOn) return [];
  const out: LevelPill[] = [];
  const multi = slices.length > 1;
  const tag = (region: string, text: string): string => (multi ? `${region.toUpperCase()} ${text}` : text);
  for (const { region, slice } of slices) {
    const level = sliceLevel(slice, nowMs);
    if (level === 'war') {
      const waves = slice.war_waves ?? null;
      const failed = slice.war_last_failed ?? null;
      const shots = slice.war_last_shots ?? null;
      const text = waves !== null && waves > 0 && failed !== null && shots !== null
        ? t('guaranteed.leg.level.war_waves', { wave: waves, failed, shots })
        : t('guaranteed.leg.level.war');
      out.push({ region, kind: 'war', text: tag(region, text), tip: t('guaranteed.leg.level.war_tip') });
    } else if (level === 'alert') {
      const since = slice.level_since ?? null;
      const text = since === null
        ? t('guaranteed.leg.level.alert')
        : t('guaranteed.leg.level.alert_since', { since: clock(since) });
      out.push({ region, kind: 'alert', text: tag(region, text), tip: t('guaranteed.leg.level.alert_tip') });
    }
    const cooldown = slice.war_cooldown_until ?? null;
    if (cooldown !== null && futureMs(cooldown, nowMs) !== null && level !== 'war') {
      const text = t('guaranteed.leg.level.cooldown', {
        until: clock(cooldown),
        n: slice.war_entries_window ?? 1,
      });
      out.push({ region, kind: 'cooldown', text: tag(region, text), tip: t('guaranteed.leg.level.cooldown_tip') });
    }
  }
  return out;
}
